import { useState, useEffect } from 'react'
import { Header } from '../components/layout/Header'

interface Statistics {
  total_requirements: number
  compliant: number
  non_compliant: number
  in_progress: number
  compliance_score: number
  audits_scheduled: number
}

interface Framework {
  framework: string
  requirements: number
  compliant: number
  score: number
  next_audit: string
}

interface Audit {
  id: string
  framework: string
  date: string
  result: string
  findings: number
}

export default function ComplianceDashboardPage() {
  const [statistics, setStatistics] = useState<Statistics | null>(null)
  const [frameworks, setFrameworks] = useState<Framework[]>([])
  const [audits, setAudits] = useState<Audit[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [statsRes, frameworksRes, auditsRes] = await Promise.all([
          fetch('/api/compliancedashboard/statistics'),
          fetch('/api/compliancedashboard/frameworks'),
          fetch('/api/compliancedashboard/audits')
        ])
        setStatistics(await statsRes.json())
        setFrameworks(await frameworksRes.json())
        setAudits(await auditsRes.json())
      } catch (error) {
        console.error('Error fetching data:', error)
      } finally {
        setLoading(false)
      }
    }
    fetchData()
  }, [])

  const getResultBadge = (result: string) => {
    const colors: Record<string, string> = {
      'Passed': 'bg-green-100 text-green-800',
      'Conditional': 'bg-yellow-100 text-yellow-800',
      'Failed': 'bg-red-100 text-red-800'
    }
    return colors[result] || 'bg-gray-100 text-gray-800'
  }

  if (loading) {
    return <div className="p-8"><Header /><div className="mt-8">Loading Compliance Dashboard...</div></div>
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <div className="p-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-8">Compliance Dashboard</h1>

        {statistics && (
          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-8">
            <div className="bg-white p-4 rounded-lg shadow">
              <div className="text-2xl font-bold text-blue-600">{statistics.total_requirements}</div>
              <div className="text-sm text-gray-500">Requirements</div>
            </div>
            <div className="bg-white p-4 rounded-lg shadow">
              <div className="text-2xl font-bold text-green-600">{statistics.compliant}</div>
              <div className="text-sm text-gray-500">Compliant</div>
            </div>
            <div className="bg-white p-4 rounded-lg shadow">
              <div className="text-2xl font-bold text-red-600">{statistics.non_compliant}</div>
              <div className="text-sm text-gray-500">Non-Compliant</div>
            </div>
            <div className="bg-white p-4 rounded-lg shadow">
              <div className="text-2xl font-bold text-yellow-600">{statistics.in_progress}</div>
              <div className="text-sm text-gray-500">In Progress</div>
            </div>
            <div className="bg-white p-4 rounded-lg shadow">
              <div className="text-2xl font-bold text-purple-600">{statistics.compliance_score}%</div>
              <div className="text-sm text-gray-500">Score</div>
            </div>
            <div className="bg-white p-4 rounded-lg shadow">
              <div className="text-2xl font-bold text-orange-600">{statistics.audits_scheduled}</div>
              <div className="text-sm text-gray-500">Audits Scheduled</div>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          <div className="bg-white rounded-lg shadow">
            <div className="p-4 border-b"><h2 className="text-lg font-semibold">Compliance Frameworks</h2></div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">Framework</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">Compliant</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">Score</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">Next Audit</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {frameworks.map((f, idx) => (
                    <tr key={idx} className="hover:bg-gray-50">
                      <td className="px-4 py-3 font-medium">{f.framework}</td>
                      <td className="px-4 py-3 text-gray-600">{f.compliant}/{f.requirements}</td>
                      <td className="px-4 py-3">
                        <span className={`font-semibold ${f.score >= 90 ? 'text-green-600' : f.score >= 80 ? 'text-yellow-600' : 'text-red-600'}`}>
                          {f.score}%
                        </span>
                      </td>
                      <td className="px-4 py-3 text-gray-600">{f.next_audit}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow">
            <div className="p-4 border-b"><h2 className="text-lg font-semibold">Recent Audits</h2></div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">Framework</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">Date</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">Result</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">Findings</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {audits.map((a) => (
                    <tr key={a.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3 font-medium">{a.framework}</td>
                      <td className="px-4 py-3 text-gray-600">{a.date}</td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-1 rounded-full text-xs ${getResultBadge(a.result)}`}>
                          {a.result}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-gray-600">{a.findings}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
